import InfoIcon from '@mui/icons-material/Info';
import {
    Box,
    IconButton,
    InputLabel,
    Popover,
    SxProps,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';

import { AXLE_TEAL } from '../../constants/colors';
import { useStateWithDerivedBoolean } from '../../utils/booleanState';
import { ClickEvent } from '../../utils/clickEvent';
import { Children, ReturnComponent, Style } from '../../utils/reactTypes';
import { If } from '../utils/conditionalComponents';
import { HorizontalStack, HorizontalStackProps } from '../utils/linearLayouts';

export const labelWithRequiredAsterisk = ({
    label,
    required,
    enabled = true,
}: {
    label: string;
    required: boolean;
    enabled?: boolean;
}): string => {
    if (required && enabled) {
        return label + ' *';
    }
    return label;
};

export const AxleLabel = ({
    label,
    required = false,
    enabled = true,
    disabledHelperText = '',
    bigLabel = false,
    style = {},
    description = undefined,
    ...rest
}: {
    label: string;
    required?: boolean;
    enabled?: boolean;
    disabledHelperText?: string;
    bigLabel?: boolean;
    style?: Style;
    description?: Children;
} & Omit<HorizontalStackProps, 'children'>): ReturnComponent => {
    const theme = useTheme();

    // Popover functionality that shows either the disabled helper text or the description.
    const showDisabledHelperTextInsteadOfDescription = !enabled && disabledHelperText !== '';
    const [popoverAnchor, setPopoverAnchor, isPopoverOpen, closePopover] =
        useStateWithDerivedBoolean<HTMLElement | null>(null);
    const popoverContents = showDisabledHelperTextInsteadOfDescription
        ? disabledHelperText
        : description;

    // Styling.
    if (bigLabel) {
        style = {
            fontSize: 20,
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 900,
            letterSpacing: '0rem',
            textTransform: 'none',
            marginBottom: 1,
            ...style,
        };
    }

    return (
        <HorizontalStack {...rest}>
            <InputLabel
                disabled={!enabled}
                sx={{
                    fontStyle: 'normal',
                    padding: theme.spacing(1, 0),
                    // textTransform: 'uppercase',
                    fontWeight: '700',
                    fontSize: '14px',
                    lineHeight: '14px',
                    color: 'black',
                    ...style,
                    '.Mui-disabled': {
                        color: 'secondary',
                    },
                }}
            >
                {labelWithRequiredAsterisk({ label, required, enabled })}
            </InputLabel>
            <If condition={!!description || showDisabledHelperTextInsteadOfDescription}>
                <IconButton
                    onMouseEnter={(event: ClickEvent) => setPopoverAnchor(event.currentTarget)}
                    onMouseLeave={() => setPopoverAnchor(null)}
                    aria-label='More information'
                >
                    <InfoIcon style={{ fontSize: 15, color: AXLE_TEAL }} />
                </IconButton>
            </If>
            <Popover
                sx={{
                    pointerEvents: 'none',
                }}
                open={isPopoverOpen}
                anchorEl={popoverAnchor}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                onClose={closePopover}
                disableRestoreFocus
            >
                <Typography variant='body2' sx={{ padding: theme.spacing(2), maxWidth: '300px' }}>
                    {popoverContents}
                </Typography>
            </Popover>
        </HorizontalStack>
    );
};

export const FieldWithAxleLabel = ({
    label,
    tooltipText,
    inlineLabel = false,
    required = false,
    enabled = true,
    disabledHelperText = '',
    description = undefined,
    children,
    sx = {},
    ...rest
}: {
    label: string | undefined;
    tooltipText?: string;
    inlineLabel?: boolean;
    required?: boolean;
    enabled?: boolean;
    disabledHelperText?: string;
    description?: Children;
    children: Children;
    sx?: SxProps;
}): ReturnComponent => (
    <>
        <Box sx={sx} {...rest}>
            <If condition={!inlineLabel}>
                <If condition={!!label}>
                    <Tooltip title={tooltipText} placement='top-end'>
                        {/* The Box component is required for the tooltip component to show up on the Axle Label. Do not remove*/}
                        <Box>
                            <AxleLabel
                                label={label ?? ''}
                                required={required}
                                description={description}
                                enabled={enabled}
                                disabledHelperText={disabledHelperText}
                            />
                        </Box>
                    </Tooltip>
                </If>
            </If>
            {children}
        </Box>
    </>
);
